import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface CheckoutSession {
  clientId: string;
  ttl?: number; // UNIX epoch seconds
  cartHash: string; // Hash of cart items to detect changes
  paymentIntentId?: string; // Payment intent ID for cancellation
}

const CHECKOUT_SESSION_KEY = 'tempefab_checkout_session';

export const useCheckoutSession = () => {
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem(CHECKOUT_SESSION_KEY);
    console.log('[CheckoutSession] Loading from localStorage:', savedSession);
    if (savedSession) {
      try {
        const parsedSession = JSON.parse(savedSession) as CheckoutSession;
        console.log('[CheckoutSession] Loaded session:', {
          clientId: parsedSession.clientId,
          cartHash: parsedSession.cartHash,
          ttl: parsedSession.ttl,
          ttlReadable: parsedSession.ttl ? new Date(parsedSession.ttl * 1000).toLocaleString() : 'Not set',
        });
        setSession(parsedSession);

        // Check if already expired
        if (parsedSession.ttl) {
          const now = Math.floor(Date.now() / 1000);
          if (now >= parsedSession.ttl) {
            console.warn('[CheckoutSession] Session already expired on load');
            setIsExpired(true);
          } else {
            console.log('[CheckoutSession] Session valid, expires in', (parsedSession.ttl - now), 'seconds');
          }
        }
      } catch (error) {
        console.error('[CheckoutSession] Failed to parse checkout session from localStorage:', error);
        localStorage.removeItem(CHECKOUT_SESSION_KEY);
      }
    } else {
      console.log('[CheckoutSession] No saved session found');
    }
  }, []);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (session) {
      localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(CHECKOUT_SESSION_KEY);
    }
  }, [session]);

  // Set up TTL timer
  useEffect(() => {
    // Clear existing timer
    if (timerRef.current) {
      console.log('[CheckoutSession] Clearing existing TTL timer');
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (session?.ttl) {
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = session.ttl - now;

      if (timeUntilExpiry > 0) {
        console.log('[CheckoutSession] Setting TTL timer for', timeUntilExpiry, 'seconds');
        // Set timer for when TTL expires
        timerRef.current = setTimeout(() => {
          console.warn('[CheckoutSession] Session expired! Redirecting to checkout-expired');
          setIsExpired(true);
        }, timeUntilExpiry * 1000); // Convert to milliseconds
      } else {
        console.warn('[CheckoutSession] TTL already expired');
        // Already expired
        setIsExpired(true);
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session?.ttl]);

  // Monitor localStorage for external session changes (e.g., when CartContext clears it)
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const storedSession = localStorage.getItem(CHECKOUT_SESSION_KEY);

      // If we have a session in state but localStorage is empty, session was cleared externally
      if (session && !storedSession) {
        console.warn('[CheckoutSession] Session was cleared externally (likely due to cart change), updating state');
        setSession(null);
        setIsExpired(false);
      }
    }, 500); // Check every 500ms

    return () => clearInterval(checkInterval);
  }, [session]);

  /**
   * Create or get existing clientId for checkout
   * @param cartHash - Hash of current cart items
   * @returns clientId for this checkout session
   */
  const initializeCheckout = useCallback((cartHash: string): string => {
    console.log('[CheckoutSession] initializeCheckout called with cartHash:', cartHash);

    // If session exists
    if (session) {
      console.log('[CheckoutSession] Existing session found:', {
        clientId: session.clientId,
        existingCartHash: session.cartHash,
        newCartHash: cartHash,
        ttl: session.ttl,
      });

      const now = Math.floor(Date.now() / 1000);

      // If cart has changed, create new session
      if (session.cartHash !== cartHash) {
        console.log('[CheckoutSession] Cart has changed, creating new session');
        const newClientId = uuidv4();
        const newSession: CheckoutSession = {
          clientId: newClientId,
          cartHash,
        };
        // Synchronously save to localStorage before updating state
        localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(newSession));
        console.log('[CheckoutSession] New session created (cart changed):', newClientId);
        setSession(newSession);
        setIsExpired(false);
        return newClientId;
      }

      // If TTL exists and is expired, regenerate
      if (session.ttl && now >= session.ttl) {
        console.log('[CheckoutSession] TTL expired, creating new session');
        const newClientId = uuidv4();
        const newSession: CheckoutSession = {
          clientId: newClientId,
          cartHash,
        };
        // Synchronously save to localStorage before updating state
        localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(newSession));
        console.log('[CheckoutSession] New session created (TTL expired):', newClientId);
        setSession(newSession);
        setIsExpired(false);
        return newClientId;
      }

      // Otherwise, reuse existing clientId
      console.log('[CheckoutSession] Reusing existing clientId:', session.clientId);
      return session.clientId;
    }

    // No session exists, create new one
    console.log('[CheckoutSession] No existing session, creating new one');
    const newClientId = uuidv4();
    const newSession: CheckoutSession = {
      clientId: newClientId,
      cartHash,
    };
    // Synchronously save to localStorage before updating state
    localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(newSession));
    console.log('[CheckoutSession] New session created:', newClientId);
    setSession(newSession);
    setIsExpired(false);
    return newClientId;
  }, [session]);

  /**
   * Update the TTL for the current session
   * @param ttl - UNIX epoch seconds when session expires
   */
  const setTTL = useCallback((ttl: number) => {
    console.log('[CheckoutSession] setTTL called:', {
      ttl,
      ttlReadable: new Date(ttl * 1000).toLocaleString(),
    });

    setSession((prevSession) => {
      if (prevSession) {
        const updatedSession = {
          ...prevSession,
          ttl,
        };
        // Synchronously save to localStorage before updating state
        localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(updatedSession));
        console.log('[CheckoutSession] TTL updated successfully for session:', prevSession.clientId);
        setIsExpired(false);
        return updatedSession;
      } else {
        console.warn('[CheckoutSession] Cannot set TTL - no session exists');
        return prevSession;
      }
    });
  }, []);

  /**
   * Clear the checkout session
   */
  const clearSession = useCallback(() => {
    console.log('[CheckoutSession] clearSession called');
    // Synchronously remove from localStorage before updating state
    localStorage.removeItem(CHECKOUT_SESSION_KEY);
    setSession(null);
    setIsExpired(false);
    if (timerRef.current) {
      console.log('[CheckoutSession] Clearing TTL timer');
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    console.log('[CheckoutSession] Session cleared successfully');
  }, []);

  /**
   * Get current clientId without initializing
   */
  const getClientId = useCallback((): string | null => {
    const clientId = session?.clientId || null;
    console.log('[CheckoutSession] getClientId called, returning:', clientId);
    return clientId;
  }, [session]);

  /**
   * Get current TTL
   */
  const getTTL = useCallback((): number | null => {
    return session?.ttl || null;
  }, [session]);

  /**
   * Set payment intent ID for the current session
   * @param paymentIntentId - Payment intent ID from backend
   */
  const setPaymentIntentId = useCallback((paymentIntentId: string) => {
    console.log('[CheckoutSession] setPaymentIntentId called:', paymentIntentId);

    setSession((prevSession) => {
      if (prevSession) {
        const updatedSession = {
          ...prevSession,
          paymentIntentId,
        };
        // Synchronously save to localStorage before updating state
        localStorage.setItem(CHECKOUT_SESSION_KEY, JSON.stringify(updatedSession));
        console.log('[CheckoutSession] Payment intent ID stored successfully');
        return updatedSession;
      } else {
        console.warn('[CheckoutSession] Cannot set payment intent ID - no session exists');
        return prevSession;
      }
    });
  }, []);

  /**
   * Get current payment intent ID
   */
  const getPaymentIntentId = useCallback((): string | null => {
    return session?.paymentIntentId || null;
  }, [session]);

  return {
    session,
    isExpired,
    initializeCheckout,
    setTTL,
    clearSession,
    getClientId,
    getTTL,
    setPaymentIntentId,
    getPaymentIntentId,
  };
};
